import { Box, Tabs } from '@mui/material';
import { DirtyTab } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloDetailView } from '@synergy/components-palo';
import { ReactElement, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { SubPageHandle } from '@synergy/utils';
import { useNavigate, useParams } from 'react-router';

import * as SupplierSpecialOffersDetailActions from './store/supplier-special-offers-detail-actions';
import { PaloDirtyProvider, usePaloDirty } from '../../../../common/components/palo/palo-dirty-context';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SupplierSpecialOffersDetailOverview } from './overview/supplier-special-offers-detail-overview';
import { SupplierSpecialOffersDetailProducts } from './products/supplier-special-offers-detail-products';
import { SupplierSpecialOffersTabs } from './models/supplier-special-offers-tabs';
import { resetSelectedLanguage } from '../../../../common/actions/selected-language-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloSupplierSpecialOffersHeader } from './components/palo-supplier-special-offers-header';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const SupplierSpecialOffersDetailInner = () => {
    const dirty = usePaloDirty();
    const suppliersSpecialOffersDetailPageRef = useRef<SubPageHandle>(null);
    const { id: supplierSpecialOffersIdParam } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();

    const [selectedTab, setSelectedTab] = useState(SupplierSpecialOffersTabs.Overview);

    const { supplierSpecialOffer, isValid: overviewIsValid } = useAppSelector(
        state => state.contentManagement.supplierSpecialOffers.detail.overview
    );

    const isNew = useMemo(() => isNaN(Number(supplierSpecialOffersIdParam)), [supplierSpecialOffersIdParam]);

    useEffect(() => {
        dispatch(resetSelectedLanguage());

        return () => {
            dispatch(SupplierSpecialOffersDetailActions.resetSupplierSpecialOffersDetail());
        };
    }, [dispatch]);

    useEffect(() => {
        if (supplierSpecialOffer?.id) {
            navigate(
                ROUTES.contentManagement.supplierSpecialOffers.detail.replace(
                    ':id',
                    supplierSpecialOffer.id.toString()
                ),
                {
                    replace: true,
                }
            );
        }
    }, [supplierSpecialOffer?.id, navigate]);

    const handleTabChange = async (_event: SyntheticEvent<Element, Event>, tab: number) => {
        if (suppliersSpecialOffersDetailPageRef.current) {
            await suppliersSpecialOffersDetailPageRef.current.submit();
        }
        setSelectedTab(tab);
    };

    const handleSave = async () => {
        if (suppliersSpecialOffersDetailPageRef.current) {
            await suppliersSpecialOffersDetailPageRef.current.validate();
            await suppliersSpecialOffersDetailPageRef.current.submit();
        }
        dispatch(SupplierSpecialOffersDetailActions.saveSupplierSpecialOffer());
    };

    const handleDelete = () => {
        dispatch(SupplierSpecialOffersDetailActions.deleteSupplierSpecialOffer());
        navigate(ROUTES.contentManagement.supplierSpecialOffers.list);
    };

    const { actions, dialogs } = usePaloSupplierSpecialOffersHeader({
        isNew,
        languageSwitchDisabled: selectedTab !== SupplierSpecialOffersTabs.Overview,
        subPageHandle: suppliersSpecialOffersDetailPageRef,
        onSave: handleSave,
        onDelete: handleDelete,
    });

    return (
        <PaloDetailView
            dirty={dirty}
            onBack={() => navigate(ROUTES.contentManagement.supplierSpecialOffers.list)}
            title={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.detailHeader" />}
            actions={actions}
            tabs={
                <Tabs value={selectedTab < 0 ? 0 : selectedTab} onChange={handleTabChange}>
                    <DirtyTab
                        value={SupplierSpecialOffersTabs.Overview}
                        label={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.tab.details" />}
                        isValid={overviewIsValid}
                    />
                    <DirtyTab
                        value={SupplierSpecialOffersTabs.Products}
                        label={<FormattedMessage id="app.contentManagement.supplierSpecialOffers.tab.products" />}
                        isValid={true}
                        disabled={!supplierSpecialOffer?.id}
                    />
                </Tabs>
            }
        >
            <Box sx={{ flex: 1, display: 'flex', overflow: 'auto' }}>
                {selectedTab === SupplierSpecialOffersTabs.Overview && (
                    <SupplierSpecialOffersDetailOverview ref={suppliersSpecialOffersDetailPageRef} />
                )}
                {selectedTab === SupplierSpecialOffersTabs.Products && <SupplierSpecialOffersDetailProducts />}
            </Box>
            {dialogs}
        </PaloDetailView>
    );
};

/** Only one tab is mounted, so the header marker covers the open form. */
export const SupplierSpecialOffersDetail = (): ReactElement => (
    <PaloDirtyProvider>
        <SupplierSpecialOffersDetailInner />
    </PaloDirtyProvider>
);
